import { isString } from 'lodash';
import Router from 'next/router';
import { createContext, memo, type ReactNode, useContext, useEffect, useState } from 'react';

import { useTrackAssetModalInfoPanelSectionToggled } from '@air/analytics';
import { reportErrorToBugsnag } from '@air/utils-error';

import { ASSET_MODAL_COLLAPSIBLE_SECTIONS } from '~/constants/localStorageKeys';
import { QueryParamNames } from '~/constants/search';
import { isDevOrTestStage } from '~/swr-hooks/utils';

type ExpandedState = { [sectionId in ExpandableSectionId]: boolean };

type CollapsibleSectionsContextType = {
  expandedSections: ExpandedState;
  toggleSection: (sectionId: ExpandableSectionId) => void;
};

const defaultExpandedState: ExpandedState = {
  boards: false,
  customFields: false,
  customMetadata: false,
  smartMetadata: false,
  fileInfo: false,
  advanced: false,
};

const CollapsibleSectionsContext = createContext<CollapsibleSectionsContextType>({
  expandedSections: defaultExpandedState,
  toggleSection: () => {},
});

export const AllExpandableSectionId = [
  'boards',
  'customFields',
  'customMetadata',
  'smartMetadata',
  'fileInfo',
  'advanced',
] as const;
export type ExpandableSectionId = (typeof AllExpandableSectionId)[number];

const isExpandableSectionId = (id: string): id is ExpandableSectionId => {
  return AllExpandableSectionId.some((sectionId) => sectionId === id);
};

export type CollapsibleSectionsContextProviderProps = {
  children: ReactNode;
};

export const CollapsibleSectionsContextProvider = memo(({ children }: CollapsibleSectionsContextProviderProps) => {
  const [expandedSections, setExpandedSections] = useState<ExpandedState>(defaultExpandedState);
  const { trackAssetModalInfoPanelSectionToggled } = useTrackAssetModalInfoPanelSectionToggled();
  // Load state on mount
  useEffect(() => {
    // Check URL query
    const searchParams = new URLSearchParams(window.location.search);
    const expandedSectionsParam = searchParams.get(QueryParamNames.expandedSections);
    const fromUrl: ExpandedState = defaultExpandedState;

    // e.g., ?expandedSections=section1,section3
    if (isString(expandedSectionsParam)) {
      const expandedList = expandedSectionsParam.split(',');
      Object.keys(defaultExpandedState).forEach((section) => {
        if (isExpandableSectionId(section)) {
          fromUrl[section] = expandedList.includes(section);
        }
      });
      setExpandedSections(fromUrl);
      localStorage.setItem(ASSET_MODAL_COLLAPSIBLE_SECTIONS, JSON.stringify(fromUrl));
      return;
    }

    // If no query, check localStorage
    const saved = localStorage.getItem(ASSET_MODAL_COLLAPSIBLE_SECTIONS);

    if (saved) {
      try {
        setExpandedSections(JSON.parse(saved));
      } catch (error) {
        if (isDevOrTestStage()) {
          throw error;
        } else {
          reportErrorToBugsnag({
            error,
            context: 'Error parsing asset modal collapsible sections',
          });
        }
      }
      return;
    }

    // Else, use defaults
    setExpandedSections(defaultExpandedState);
  }, []);

  // Toggle a section
  const toggleSection = (sectionId: ExpandableSectionId) => {
    setExpandedSections((prev) => {
      const newState = { ...prev, [sectionId]: !prev[sectionId] };
      trackAssetModalInfoPanelSectionToggled({ section: sectionId, isExpanded: newState[sectionId] });

      // Update URL query
      const expandedList = Object.keys(newState).filter((id) => isExpandableSectionId(id) && newState[id]);
      const query = { ...Router.query };

      if (expandedList.length > 0) {
        query.expanded = expandedList.join(',');
      } else {
        delete query.expanded;
      }
      Router.push({ query }, undefined, { shallow: true });

      // Save to localStorage
      localStorage.setItem(ASSET_MODAL_COLLAPSIBLE_SECTIONS, JSON.stringify(newState));

      return newState;
    });
  };

  return (
    <CollapsibleSectionsContext.Provider value={{ expandedSections, toggleSection }}>
      {children}
    </CollapsibleSectionsContext.Provider>
  );
});

CollapsibleSectionsContextProvider.displayName = 'CollapsibleSectionsContextProvider';

export const useCollapsibleSectionsContext = () => {
  const context = useContext(CollapsibleSectionsContext);

  if (!context) {
    const error = new Error('useCollapsibleSectionsContext must be used within CollapsibleSectionsContextProvider');

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: 'Collapsible sections context not found',
      });
    }
  }

  return context;
};
